/**
 * Validation utility functions
 */

/**
 * Check if current product/price is free
 * @param {Object} formData - The form data object
 * @param {boolean} isDevResource - Whether this is a dev resource
 * @returns {boolean} - True if product is free
 */
export const isCurrentProductFree = (formData, isDevResource) => {
  // Standardized: free products are determined by:
  // 1. Dev resources are always free
  // 2. Prices with unit_amount === 0 (checked via lookup key pattern for now)
  // 3. Partner free prices (lookup key ends with '-partner-free')
  const priceKey = String(formData.configuration?.price_lookup_key || '');
  return (priceKey && priceKey.endsWith && priceKey.endsWith('-partner-free')) || // Partner free prices
         isDevResource; // Dev resources are free
};

/**
 * Check if cost pool is required based on price metadata first, then product metadata
 * @param {Object} formData - The form data object
 * @param {Array} products - Array of available products
 * @param {Array} prices - Array of available prices
 * @returns {boolean} - True if cost pool is required
 */
export const isCostPoolRequired = (formData, products, prices = []) => {
  const productId = formData.configuration?.product_id;
  const priceLookupKey = formData.configuration?.price_lookup_key;
  
  if (!productId) return true; // Default to required if no product selected
  
  // First, check price metadata if a price is selected
  if (priceLookupKey && prices.length > 0) {
    const selectedPrice = prices.find(p => 
      p.lookup_key === priceLookupKey || 
      p.id === priceLookupKey ||
      (p.metadata && Object.keys(p.metadata).length > 0 && p.lookup_key === priceLookupKey)
    );
    
    if (selectedPrice && selectedPrice.metadata) {
      const priceCostPool = selectedPrice.metadata.cost_pool;
      // Check if price metadata explicitly sets cost_pool
      if (priceCostPool === 'false' || priceCostPool === false) {
        return false; // Cost pool not required based on price metadata
      }
      if (priceCostPool === 'true' || priceCostPool === true) {
        return true; // Cost pool required based on price metadata
      }
    }
  }
  
  // Fallback to product metadata
  const selectedProduct = products.find(p => p.id === productId);
  if (!selectedProduct) return true; // Default to required if product not found
  
  // Check if metadata.cost_pool is explicitly false
  const costPoolMetadata = selectedProduct.metadata?.cost_pool;
  if (costPoolMetadata === false || costPoolMetadata === 'false') {
    return false; // Cost pool not required
  }
  
  return true; // Default to required
};

/**
 * Validate form data for create mode
 * @param {Object} formData - The form data to validate
 * @param {string} mode - The current mode ('view', 'create', 'edit')
 * @param {boolean} isDevTenant - Whether current tenant is a dev tenant
 * @param {boolean} isSelectedTenantPartner - Whether selected tenant is a partner
 * @param {boolean} isDevResource - Whether this is a dev resource
 * @param {Function} isCurrentProductFreeFn - Function to check if product is free
 * @returns {boolean} - True if form is valid
 */
export const isFormValid = (
  formData,
  mode,
  isDevTenant,
  isSelectedTenantPartner,
  isDevResource,
  isCurrentProductFreeFn
) => {
  if (mode !== 'create') return true;
  
  const baseValidation = (
    formData.name && 
    formData.name.trim() && 
    formData.tenant_id && 
    formData.configuration?.product_id &&
    formData.configuration?.price_lookup_key
  );
  
  // Only require cost pool if not a dev tenant, not a dev resource, not a partner tenant, and not a free product
  if (isDevTenant || isSelectedTenantPartner || isDevResource || isCurrentProductFreeFn()) {
    return baseValidation;
  } else {
    return baseValidation && formData.cost_pool_id;
  }
};

